import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api from "../api";

function sessionId() {
  let s = localStorage.getItem("cami_session");
  if (!s) { s = `cami_${Math.random().toString(36).slice(2)}_${Date.now()}`; localStorage.setItem("cami_session", s); }
  return s;
}

export default function Cami() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get(`/cami/history/${sid.current}`).then((r) => {
        if (r.data.length) setMessages(r.data);
        else setMessages([{ role: "assistant", text: "Hi, I'm Cami! Ask me anything about onboarding, documents, training, or getting certified." }]);
      }).catch(() => setMessages([{ role: "assistant", text: "Hi, I'm Cami! How can I help with your onboarding?" }]));
    }
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/cami/chat", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again or contact support@campbellconnect.com." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-violet-600 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-sm font-bold">C</div>
              <div>
                <div className="text-sm font-semibold leading-tight">Cami</div>
                <div className="text-[11px] text-white/70">Onboarding assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="rounded-md p-1 hover:bg-white/20"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-violet-600 text-white" : "border border-slate-200 bg-white text-slate-700"}`}>{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="rounded-2xl border border-slate-200 bg-white px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-violet-500" /></div></div>
            )}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-2 border-t border-slate-200 p-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Cami…"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-violet-400"
            />
            <button onClick={send} disabled={busy} className="btn-primary-gradient flex h-9 w-9 items-center justify-center rounded-xl text-white disabled:opacity-60"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} className="btn-primary-gradient fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full px-4 py-3 text-white shadow-lg">
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="text-sm font-semibold">Cami</span>
      </button>
    </>
  );
}
